import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Check, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../api/client';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { lostReasonLabel, stageSkin } from '../../lib/opportunity';
import type { StageProposal, StageProposalDecision } from '../../types/opportunity';
import { OpportunityStageBadge } from './OpportunityStageBadge';

/**
 * One Scout stage-change proposal (api#1174), with Approve / Reject.
 *
 * A proposal is a PENDING agent run; the deal has NOT moved. Approve moves it
 * through the API's one write path, Reject changes only the run. Shown on the
 * deal's page and in the agent activity panel — the same card in both, so the
 * two cannot disagree about what Scout proposed.
 *
 * 403: only the deal's owner, an admin or office staff decides — `canDecide`
 * hides the buttons for anyone else and says so. 409: already decided, or the
 * deal moved since and the run was superseded; the card says which the API
 * reported and drops out on the refetch.
 */
export function StageProposalCard({
  proposal,
  canDecide,
  showDeal = false,
  onDecided,
}: {
  proposal: StageProposal;
  canDecide: boolean;
  /** In the review queue: name the deal and link to it. */
  showDeal?: boolean;
  onDecided?: (decision: StageProposalDecision | null) => void;
}) {
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();
  const [error, setError] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'reject') =>
      (decision === 'approve'
        ? api.opportunities.approveStageProposal(proposal.runId)
        : api.opportunities.rejectStageProposal(proposal.runId)
      ).then((r) => r.data.data as StageProposalDecision),
    onSuccess: (res, decision) => {
      toast.success(decision === 'approve' ? 'Stage change approved' : 'Proposal rejected');
      refresh();
      onDecided?.(res ?? null);
    },
    onError: (err: any) => {
      setError(getApiErrorMessage(err, 'Could not decide this proposal.'));
      // 409: already decided or superseded — the list is stale either way.
      if (err?.response?.status === 409) refresh();
    },
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['opportunities'] });
    queryClient.invalidateQueries({ queryKey: ['opportunity', proposal.opportunityId] });
    queryClient.invalidateQueries({ queryKey: ['stage-proposals'] });
    if (proposal.toStage === 'won') queryClient.invalidateQueries({ queryKey: ['customers'] });
  }

  const lost = proposal.toStage === 'lost';
  const lostLabel = lostReasonLabel(proposal.lostReason);

  return (
    <article
      data-testid="stage-proposal-card"
      aria-label="Scout stage proposal"
      className={`rounded-fi-panel border border-fieldiq-border border-l-4 bg-fieldiq-card p-4 ${stageSkin(proposal.toStage).rail}`}
    >
      <div className="flex flex-wrap items-center gap-2 text-xs font-black uppercase tracking-[0.14em] text-fieldiq-muted">
        <Sparkles className="size-3.5" aria-hidden="true" /> Scout proposes a stage change
        <span className="font-semibold normal-case tracking-normal">· {formatInstant(proposal.proposedAt, timeZone) ?? '—'}</span>
      </div>
      {showDeal && (
        <p className="mt-2 text-sm font-extrabold text-white">
          {proposal.opportunityId ? (
            <Link to={`/sales/pipeline/${proposal.opportunityId}`} className="hover:underline">{proposal.title ?? '—'}</Link>
          ) : (proposal.title ?? '—')}
          <span className="font-normal text-fieldiq-muted"> · {proposal.customerName ?? '—'}</span>
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <OpportunityStageBadge stage={proposal.fromStage} />
        <ArrowRight className="size-4 text-fieldiq-muted" aria-label="to" />
        <OpportunityStageBadge stage={proposal.toStage} />
      </div>
      <p className="mt-2 text-sm text-fieldiq-text">{proposal.reason ?? '—'}</p>
      {lost && (
        <p className="mt-1 text-sm text-fieldiq-text">
          Lost because: {lostLabel ?? '—'}{proposal.lostReasonNote ? ` — ${proposal.lostReasonNote}` : ''}
        </p>
      )}
      {error && <p role="alert" className="mt-2 text-sm text-fieldiq-text">{error}</p>}
      {canDecide ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={decide.isPending}
            onClick={() => { setError(null); decide.mutate('approve'); }}
            className="fi-action fi-action--standard fi-action--primary min-h-[44px] disabled:opacity-50"
          >
            <Check className="size-4" aria-hidden="true" /> Approve
          </button>
          <button
            type="button"
            disabled={decide.isPending}
            onClick={() => { setError(null); decide.mutate('reject'); }}
            className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-fieldiq-border bg-fieldiq-card px-4 text-sm font-semibold text-fieldiq-text hover:text-white disabled:opacity-50"
          >
            <X className="size-4" aria-hidden="true" /> Reject
          </button>
        </div>
      ) : (
        <p className="mt-3 text-xs text-fieldiq-muted">Only the deal's owner, an admin or office staff can approve or reject this.</p>
      )}
    </article>
  );
}

export default StageProposalCard;
